import React from "react";

import { InfoMessage, Spinner } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { StatTile } from "@/components/molecules/StatTile/StatTile";

import { formatPrice } from "@/utils/USubscription";

import { PLAN_NAMES } from "@/constants/CPlans";
import { adminKeys } from "@/constants/CQueryKeys";

import { TPlanCode } from "@/types/TSubscription";

const PLAN_ORDER: TPlanCode[] = [
	"free",
	"user",
	"user_plus",
	"team",
	"team_plus",
	"enterprise",
];

/** The numbers: who is on what, trials running out, and what the paying ones add up to. */
export const AdminMetrics: React.FC = () => {
	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.metrics,
		queryFn: AdminService.metrics,
	});

	if (isLoading) return <Spinner />;
	if (error || !data)
		return <InfoMessage color="danger">{t("admin.error")}</InfoMessage>;

	const total = Object.values(data.workspacesByPlan).reduce(
		(sum, count) => sum + (count ?? 0),
		0
	);

	return (
		<div className="flex flex-col gap-6">
			<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatTile
					label={t("admin.metrics.mrr")}
					value={formatPrice(data.mrr, "eur")}
					sub={t("admin.metrics.mrrSub")}
				/>
				<StatTile
					label={t("admin.metrics.paying")}
					value={data.paying}
					sub={t("admin.metrics.payingSub")}
				/>
				<StatTile
					label={t("admin.metrics.trials")}
					value={data.trialsRunning}
					sub={t("admin.metrics.trialsSub", {
						count: data.trialsEndingSoon,
					})}
				/>
				<StatTile
					label={t("admin.metrics.accounts")}
					value={total}
					sub={t("admin.metrics.accountsSub")}
				/>
			</div>
			<div className="flex flex-col gap-2">
				<h2 className="text-sm font-medium">
					{t("admin.metrics.byPlan")}
				</h2>
				<ul className="grid gap-2 sm:grid-cols-3 xl:grid-cols-6">
					{PLAN_ORDER.map((code) => (
						<li
							key={code}
							className="flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 text-sm"
							data-testid={`plan-count-${code}`}
						>
							<span>{PLAN_NAMES[code]}</span>
							<span className="font-medium">
								{data.workspacesByPlan[code] ?? 0}
							</span>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
